import { BaseService } from './baseService';

export interface QueryFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'between' | 'in' | 'not_in';
  value: unknown;
}

export interface ReportQuery {
  entity: 'form_submissions' | 'clients' | 'loans';
  filters: QueryFilter[];
  dateRange?: {
    field: string;
    start?: string;
    end?: string;
  };
  groupBy?: string[];
  aggregations?: {
    field: string;
    function: 'count' | 'sum' | 'avg' | 'min' | 'max';
  }[];
  sortBy?: {
    field: string;
    direction: 'asc' | 'desc';
  };
  limit?: number;
  offset?: number;
}

export interface ReportResult {
  data: unknown[];
  grouped?: Record<string, unknown>;
  aggregations?: Record<string, number>;
  totalCount: number;
  count: number;
  query: ReportQuery;
}

export interface AIQueryRequest {
  naturalLanguageQuery: string;
  context?: {
    userId?: string;
    recentQueries?: ReportQuery[];
    availableData?: string[];
  };
}

export interface AIQueryResponse {
  structuredQuery: ReportQuery;
  explanation: string;
  confidence: number;
  suggestions: string[];
  insights?: string[];
}

export interface AIQuerySuggestion {
  query: string;
  description: string;
  category: 'financial' | 'operational' | 'compliance' | 'insights';
  complexity: 'simple' | 'intermediate' | 'advanced';
}

export interface FormSubmission {
  id: string;
  formId: string;
  formVersion: string;
  submissionData: Record<string, unknown>;
  submittedAt: string;
  status: string;
  validationErrors: Record<string, string[]> | null;
  metadata?: Record<string, unknown>;
  rulesExecuted?: string[];
  workflowState?: WorkflowState;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowState {
  currentStatus: string;
  stage: string;
  completedStages: string[];
  nextActions: string[];
  assignedTo?: string;
  lastUpdated: string;
  history: WorkflowHistoryEntry[];
}

export interface WorkflowHistoryEntry {
  timestamp: string;
  fromStatus: string;
  toStatus: string;
  triggeredBy: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}

export interface FormSubmissionsReportParams {
  formId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  hasWorkflowState?: boolean;
  workflowStatus?: string;
  groupBy?: string;
  aggregate?: boolean;
}

class ReportingService extends BaseService {
  /**
   * Create a custom report using the query builder
   */
  async createReport(query: ReportQuery): Promise<ReportResult> {
    const response = await this.api.post<ReportResult>('/reports/create', query);
    return response.data;
  }

  /**
   * Get form submissions report with filters and aggregations
   */
  async getFormSubmissionsReport(params: FormSubmissionsReportParams = {}): Promise<{
    data: FormSubmission[];
    grouped?: Record<string, FormSubmission[]>;
    aggregations?: Record<string, unknown>;
    totalCount: number;
  }> {
    const response = await this.api.get('/reports/submissions', { params });
    return response.data;
  }

  /**
   * Export report data in various formats
   */
  async exportReport(query: ReportQuery, format: 'json' | 'csv' | 'xlsx' = 'json'): Promise<Blob> {
    const response = await this.api.post('/reports/export', query, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Update workflow state for a form submission
   */
  async updateWorkflowState(
    submissionId: string, 
    updates: {
      status?: string;
      stage?: string;
      assignedTo?: string;
      reason?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<{
    submissionId: string;
    workflowState: WorkflowState;
  }> {
    const response = await this.api.put(`/submissions/${submissionId}/workflow`, updates);
    return response.data;
  }

  /**
   * Get workflow history for a form submission
   */
  async getWorkflowHistory(submissionId: string): Promise<{
    submissionId: string;
    workflowState: WorkflowState;
    history: WorkflowHistoryEntry[];
  }> {
    const response = await this.api.get(`/submissions/${submissionId}/workflow/history`);
    return response.data;
  }

  /**
   * Get available fields for reporting based on entity type
   */
  getAvailableFields(entity: ReportQuery['entity']): {
    field: string;
    label: string;
    type: 'text' | 'number' | 'date' | 'boolean' | 'object';
  }[] {
    const fieldMaps = {
      form_submissions: [
        { field: 'id', label: 'Submission ID', type: 'text' as const },
        { field: 'formId', label: 'Form ID', type: 'text' as const },
        { field: 'formVersion', label: 'Form Version', type: 'text' as const },
        { field: 'status', label: 'Status', type: 'text' as const },
        { field: 'submittedAt', label: 'Submitted At', type: 'date' as const },
        { field: 'workflowState.currentStatus', label: 'Workflow Status', type: 'text' as const },
        { field: 'workflowState.stage', label: 'Workflow Stage', type: 'text' as const },
        { field: 'workflowState.assignedTo', label: 'Assigned To', type: 'text' as const },
        { field: 'rulesExecuted', label: 'Rules Executed', type: 'object' as const },
        { field: 'createdAt', label: 'Created At', type: 'date' as const },
        { field: 'updatedAt', label: 'Updated At', type: 'date' as const }
      ],
      clients: [
        { field: 'id', label: 'Client ID', type: 'text' as const },
        { field: 'businessName', label: 'Business Name', type: 'text' as const },
        { field: 'ein', label: 'EIN', type: 'text' as const },
        { field: 'businessType', label: 'Business Type', type: 'text' as const },
        { field: 'industry', label: 'Industry', type: 'text' as const },
        { field: 'createdAt', label: 'Created At', type: 'date' as const },
        { field: 'updatedAt', label: 'Updated At', type: 'date' as const }
      ],
      loans: [
        { field: 'id', label: 'Loan ID', type: 'text' as const },
        { field: 'clientId', label: 'Client ID', type: 'text' as const },
        { field: 'amount', label: 'Loan Amount', type: 'number' as const },
        { field: 'status', label: 'Status', type: 'text' as const },
        { field: 'term', label: 'Term (months)', type: 'number' as const },
        { field: 'interestRate', label: 'Interest Rate', type: 'number' as const },
        { field: 'createdAt', label: 'Created At', type: 'date' as const },
        { field: 'updatedAt', label: 'Updated At', type: 'date' as const }
      ]
    };

    return fieldMaps[entity] || [];
  }

  /**
   * Get suggested filters based on data patterns
   */
  getSuggestedFilters(entity: ReportQuery['entity']): Partial<QueryFilter>[] {
    const filterSuggestions = {
      form_submissions: [
        { field: 'status', operator: 'equals' as const },
        { field: 'submittedAt', operator: 'between' as const },
        { field: 'workflowState.currentStatus', operator: 'equals' as const },
        { field: 'formId', operator: 'equals' as const }
      ],
      clients: [
        { field: 'businessType', operator: 'equals' as const },
        { field: 'industry', operator: 'equals' as const },
        { field: 'createdAt', operator: 'between' as const }
      ],
      loans: [
        { field: 'status', operator: 'equals' as const },
        { field: 'amount', operator: 'greater_than' as const },
        { field: 'createdAt', operator: 'between' as const },
        { field: 'term', operator: 'equals' as const }
      ]
    };

    return filterSuggestions[entity] || [];
  }

  /**
   * Convert natural language query to structured query using AI
   */
  async convertNaturalLanguageQuery(request: AIQueryRequest): Promise<AIQueryResponse> {
    const response = await this.api.post<AIQueryResponse>('/reports/ai/convert', request);
    return response.data;
  }

  /**
   * Get AI-powered query suggestions
   */
  async getAIQuerySuggestions(): Promise<{ suggestions: AIQuerySuggestion[] }> {
    const response = await this.api.get<{ suggestions: AIQuerySuggestion[] }>('/reports/ai/suggestions');
    return response.data;
  }

  /**
   * Generate AI insights from query results
   */
  async generateAIInsights(query: ReportQuery, results: unknown[]): Promise<{ insights: string[] }> {
    const response = await this.api.post<{ insights: string[] }>('/reports/ai/insights', {
      query,
      results
    });
    return response.data;
  }
}

export default new ReportingService();